import type {
  FormSchemaVersion,
  RogerDocFormTypeCatalogEntry,
  BespokeTemplateImportUpsertRow,
} from '../../services/api/types.gen';
import type { RogerDocFormType } from '../../services/api/models/RogerDocFormType';
import type { BespokeTemplateExportRow } from './bespokeTemplatesExport';
import { compareFormGroupKeys } from './formPrecedence';

/** Schema version strings that do not appear in any loaded catalog slice group here. */
export const IMPORT_UNKNOWN_FORM_TYPE = '__import_unknown__' as const;

const compareUnknownLast = compareFormGroupKeys(IMPORT_UNKNOWN_FORM_TYPE);

export type ImportFormTypeGroupingKey =
  RogerDocFormType | typeof IMPORT_UNKNOWN_FORM_TYPE;

/** Stable leaf id for bundle rows: the upsert key, pin + section. */
export function bespokeTemplateLeafKey(row: {
  form_type: string;
  schema_version_name: string;
  form_schema_version: string;
  section_name: string;
}): string {
  return [
    row.form_type.trim(),
    row.schema_version_name.trim(),
    row.form_schema_version.trim(),
    row.section_name.trim(),
  ].join('\0');
}

/** Sort order for bundle rows: by pin + section, ignoring case. */
export function compareBespokeTemplateLeafKeys(
  a: Parameters<typeof bespokeTemplateLeafKey>[0],
  b: Parameters<typeof bespokeTemplateLeafKey>[0]
): number {
  return bespokeTemplateLeafKey(a).localeCompare(
    bespokeTemplateLeafKey(b),
    undefined,
    { sensitivity: 'base' }
  );
}

export function exportRowsToImportUpserts(
  rows: BespokeTemplateExportRow[]
): BespokeTemplateImportUpsertRow[] {
  return rows.map(row => ({
    form_type: row.form_type,
    schema_version_name: row.schema_version_name.trim(),
    form_schema_version: row.form_schema_version.trim(),
    section_name: row.section_name.trim(),
    layout_json: row.layout_json ?? undefined,
    description: row.description ?? null,
  }));
}

export type BespokeParsedImportEnvelope = {
  formatVersion: number;
  /** `true` for `format_version` <= 4 — rows need {@link resolveLegacyImportRowPins}. */
  needsPinInference: boolean;
  templates: BespokeTemplateExportRow[];
  exportScope?: string;
  exportedAt?: string;
  selectedFormTypes?: RogerDocFormType[];
};

function requireString(rec: Record<string, unknown>, k: string): string {
  const v = rec[k];
  if (typeof v !== 'string' || !v.trim()) {
    throw new Error(`Invalid template row: missing or empty "${k}"`);
  }
  return v.trim();
}

/**
 * `format_version` <= 4 bundles predate the RogerDoc pin — rows only carry
 * `schema_version_name`. Rows are coerced with this placeholder and resolved
 * against the current catalog by {@link resolveLegacyImportRowPins} once it's
 * available (ambiguous names can't be resolved and are reported separately).
 */
const UNRESOLVED_LEGACY_FORM_TYPE = '' as RogerDocFormType;

function coerceTemplateExportRow(
  item: unknown,
  formatVersion: number
): BespokeTemplateExportRow {
  if (!item || typeof item !== 'object') {
    throw new Error('Invalid templates[] entry (expected object)');
  }
  const r = item as Record<string, unknown>;
  const hasPinFields = formatVersion >= 5;
  return {
    form_type: hasPinFields
      ? (requireString(r, 'form_type') as RogerDocFormType)
      : UNRESOLVED_LEGACY_FORM_TYPE,
    schema_version_name: requireString(r, 'schema_version_name'),
    form_schema_version: hasPinFields
      ? requireString(r, 'form_schema_version')
      : '',
    section_name: requireString(r, 'section_name'),
    layout_json: (() => {
      if (!('layout_json' in r)) return undefined;
      if (r.layout_json == null) return null;
      if (!Array.isArray(r.layout_json)) {
        throw new Error(
          `Invalid layout_json for "${requireString(r, 'section_name')}": expected array or null, got ${typeof r.layout_json}`
        );
      }
      return r.layout_json as unknown[];
    })(),
    description:
      r.description === null || typeof r.description === 'string'
        ? (r.description as string | null)
        : undefined,
  };
}

/** Human-readable pin + section for import errors. */
function describeImportSection(row: BespokeTemplateExportRow): string {
  const pin = [row.form_type, row.schema_version_name, row.form_schema_version]
    .map(part => part.trim())
    .filter(Boolean)
    .join(' · ');
  return `"${row.section_name}" (${pin})`;
}

/**
 * Parses bundled exports: ``format_version`` 1–6. v6 rows are one layout per
 * section, and the last duplicate wins. Older bundles carried standby rows, so
 * only their `live` rows are read, and two live rows for one section refuse
 * the bundle. v5+ rows carry the full pin; v≤4 rows need
 * {@link resolveLegacyImportRowPins} before they can be submitted.
 */
export function parseBespokeImportBundleJson(
  raw: unknown
): BespokeParsedImportEnvelope {
  if (!raw || typeof raw !== 'object') {
    throw new Error('JSON root must be an object');
  }
  const root = raw as Record<string, unknown>;
  const fv = root.format_version;
  if (fv !== 1 && fv !== 2 && fv !== 3 && fv !== 4 && fv !== 5 && fv !== 6) {
    throw new Error(
      `Unsupported format_version (${String(fv)}). Expected 1–6.`
    );
  }

  const templatesRaw = root.templates;
  if (!Array.isArray(templatesRaw)) {
    throw new Error('Missing "templates" array');
  }

  const byKey = new Map<string, BespokeTemplateExportRow>();
  let index = 0;
  for (const item of templatesRaw) {
    index += 1;
    let row: BespokeTemplateExportRow;
    try {
      row = coerceTemplateExportRow(item, fv);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      throw new Error(`templates[${index - 1}]: ${msg}`);
    }
    const key = bespokeTemplateLeafKey(row);
    if (fv < 6) {
      if ((item as Record<string, unknown>).status !== 'live') continue;
      if (byKey.has(key)) {
        throw new Error(
          `The bundle has two live layouts for section ${describeImportSection(row)}. Keep one and import again.`
        );
      }
    }
    byKey.set(key, row);
  }

  const selectedFormTypes =
    Array.isArray(root.selected_form_types) &&
    root.selected_form_types.every(t => typeof t === 'string')
      ? [...(root.selected_form_types as RogerDocFormType[])]
      : undefined;

  return {
    formatVersion: fv,
    needsPinInference: fv < 5,
    templates: [...byKey.values()].sort(compareBespokeTemplateLeafKeys),
    exportScope:
      typeof root.export_scope === 'string' ? root.export_scope : undefined,
    exportedAt:
      typeof root.exported_at === 'string' ? root.exported_at : undefined,
    selectedFormTypes,
  };
}

/** Rows resolved against the current catalog, split from those needing manual fixup. */
export type LegacyPinResolution = {
  rows: BespokeTemplateExportRow[];
  unresolved: BespokeTemplateExportRow[];
};

/**
 * Legacy bundles (`format_version` <= 4) don't carry `form_type` /
 * `form_schema_version` per row. Infers them from the current catalog when
 * `schema_version_name` maps to exactly one `{form_type, form_schema_version}`
 * pin; rows whose name is unknown, shared across form types, or shared across
 * multiple versions within one form type are returned as `unresolved`.
 */
export function resolveLegacyImportRowPins(params: {
  rows: BespokeTemplateExportRow[];
  catalog: RogerDocFormTypeCatalogEntry[];
  versionsByFormType: Map<RogerDocFormType, FormSchemaVersion[]>;
}): LegacyPinResolution {
  // name -> formType -> distinct form_schema_version values in the catalog.
  const versionsByName = new Map<string, Map<RogerDocFormType, Set<string>>>();
  for (const entry of params.catalog) {
    const ft = entry.form_type as RogerDocFormType;
    for (const v of params.versionsByFormType.get(ft) ?? []) {
      const nm = (v.name ?? '').trim();
      const ver = (v.version ?? '').trim();
      if (!nm || !ver) continue;
      let byFormType = versionsByName.get(nm);
      if (!byFormType) {
        byFormType = new Map();
        versionsByName.set(nm, byFormType);
      }
      let versions = byFormType.get(ft);
      if (!versions) {
        versions = new Set();
        byFormType.set(ft, versions);
      }
      versions.add(ver);
    }
  }

  const rows: BespokeTemplateExportRow[] = [];
  const unresolved: BespokeTemplateExportRow[] = [];
  for (const row of params.rows) {
    if (row.form_type) {
      rows.push(row);
      continue;
    }
    const trimmedName = row.schema_version_name.trim();
    const byFormType = versionsByName.get(trimmedName);
    if (!byFormType || byFormType.size !== 1) {
      unresolved.push(row);
      continue;
    }
    const [[ft, versions]] = byFormType;
    if (versions.size !== 1) {
      unresolved.push(row);
      continue;
    }
    const [formSchemaVersion] = versions;
    rows.push({
      ...row,
      form_type: ft,
      form_schema_version: formSchemaVersion,
    });
  }
  return { rows, unresolved };
}

export type ImportSchemaGroup = {
  schema_version_name: string;
  form_schema_version: string;
  /** One row per section: the section is the tree's leaf. */
  sections: BespokeTemplateExportRow[];
};

export type ImportFormTypeGroup = {
  formTypeKey: ImportFormTypeGroupingKey;
  formTypeDisplayLabel: string;
  schemas: ImportSchemaGroup[];
};

const byText = (a: string, b: string) =>
  a.localeCompare(b, undefined, { sensitivity: 'base' });

/** Groups rows for nested checkbox UI: form type → schema version → section. */
export function buildImportNestedGroups(params: {
  templates: BespokeTemplateExportRow[];
  catalog: RogerDocFormTypeCatalogEntry[];
}): ImportFormTypeGroup[] {
  const displayByFormType = new Map<
    RogerDocFormType,
    RogerDocFormTypeCatalogEntry['display_label']
  >();
  for (const row of params.catalog) {
    displayByFormType.set(row.form_type as RogerDocFormType, row.display_label);
  }

  const acc = new Map<string, Map<string, ImportSchemaGroup>>();
  for (const tmpl of params.templates) {
    // Rows are expected to already carry a resolved `form_type` (see
    // `resolveLegacyImportRowPins` for legacy bundles); anything still
    // unresolved falls into the "unknown" bucket rather than crashing.
    const formMapKey: string = tmpl.form_type || IMPORT_UNKNOWN_FORM_TYPE;
    const schema_version_name = tmpl.schema_version_name.trim();
    const form_schema_version = tmpl.form_schema_version.trim();
    let bySchema = acc.get(formMapKey);
    if (!bySchema) {
      bySchema = new Map();
      acc.set(formMapKey, bySchema);
    }
    const schemaKey = `${schema_version_name}\0${form_schema_version}`;
    let group = bySchema.get(schemaKey);
    if (!group) {
      group = { schema_version_name, form_schema_version, sections: [] };
      bySchema.set(schemaKey, group);
    }
    group.sections.push(tmpl);
  }

  function labelFor(formMapKey: string): {
    key: ImportFormTypeGroupingKey;
    display: string;
  } {
    if (formMapKey === IMPORT_UNKNOWN_FORM_TYPE) {
      return {
        key: IMPORT_UNKNOWN_FORM_TYPE,
        display:
          'Unknown form type (schema version name not in current catalog)',
      };
    }
    const ft = formMapKey as RogerDocFormType;
    return { key: ft, display: displayByFormType.get(ft) ?? formMapKey };
  }

  return (
    [...acc.entries()]
      // The unknown bucket sorts last whatever it is named; everything else
      // follows the same precedence order as the dropdown and the validation
      // results, so a form sits in the same place in every list.
      .sort(([a], [b]) => compareUnknownLast(a, b))
      .map(([formMapKey, bySchema]) => {
        const { key: formTypeKey, display: formTypeDisplayLabel } =
          labelFor(formMapKey);
        const schemas = [...bySchema.values()].sort(
          (a, b) =>
            byText(a.schema_version_name, b.schema_version_name) ||
            byText(a.form_schema_version, b.form_schema_version)
        );
        for (const schema of schemas) {
          schema.sections.sort((a, b) =>
            byText(a.section_name, b.section_name)
          );
        }
        return { formTypeKey, formTypeDisplayLabel, schemas };
      })
  );
}

export function bespokeRowsByLeafKey(
  rows: BespokeTemplateExportRow[]
): Map<string, BespokeTemplateExportRow> {
  const m = new Map<string, BespokeTemplateExportRow>();
  for (const row of rows) {
    m.set(bespokeTemplateLeafKey(row), row);
  }
  return m;
}

export function leafKeysUnderSchema(sch: ImportSchemaGroup): string[] {
  return sch.sections.map(bespokeTemplateLeafKey);
}

export function leafKeysUnderFormType(ft: ImportFormTypeGroup): string[] {
  return ft.schemas.flatMap(leafKeysUnderSchema);
}

export function collectLeafKeysFromGroups(
  groups: ImportFormTypeGroup[]
): string[] {
  return groups.flatMap(leafKeysUnderFormType);
}

/**
 * Serializable row model for `SelectableNestedTable` (collapsible grouped rows +
 * divider chrome). Intended for bundles like bespoke template import previews.
 */
export type SelectableNestedTableRowModel = {
  /** Stable DOM / React key */
  id: string;
  /** Leaf keys governed by this row’s checkbox */
  branchLeafKeys: readonly string[];
  primaryLine: string;
  /** Inline taxonomy label (shown before primary text on parent rows). */
  categoryLine?: string | null;
  /** Bold primary text (form type tier only — other tiers stay regular weight). */
  primaryBold?: boolean;
  /** Muted identifier shown after the primary label (e.g. `RogerDocFormType` key). */
  primaryMeta?: string | null;
  children?: SelectableNestedTableRowModel[];
};

export function importNestedGroupsToSelectableNestedRows(
  groups: ImportFormTypeGroup[],
  formatSectionLabel: (sectionMachineName: string) => string
): SelectableNestedTableRowModel[] {
  return groups.map((ft, ftIx) => ({
    id: `import-ft-${ftIx}`,
    branchLeafKeys: leafKeysUnderFormType(ft),
    primaryLine: ft.formTypeDisplayLabel,
    categoryLine: 'Form type',
    primaryBold: true,
    children: ft.schemas.map((sch, schIx) => ({
      id: `import-ft-${ftIx}-sch-${schIx}`,
      branchLeafKeys: leafKeysUnderSchema(sch),
      primaryLine: sch.schema_version_name,
      primaryMeta: sch.form_schema_version || null,
      categoryLine: 'Schema version',
      children: sch.sections.map((row, secIx) => ({
        id: `import-ft-${ftIx}-sch-${schIx}-sec-${secIx}`,
        branchLeafKeys: [bespokeTemplateLeafKey(row)],
        primaryLine: formatSectionLabel(row.section_name),
        categoryLine: 'Section',
      })),
    })),
  }));
}

/** `true` | `false` | `'indeterminate'` from Radix Checkbox. */
export function triStateFromSelected(
  allKeys: string[],
  selected: ReadonlySet<string>
): boolean | 'indeterminate' {
  let n = 0;
  for (const k of allKeys) {
    if (selected.has(k)) n += 1;
  }
  if (n === 0) return false;
  if (n === allKeys.length) return true;
  return 'indeterminate';
}
